import { useCallback, useEffect, useReducer } from 'react';
import { v4 as uuid } from 'uuid';
import {
  ClassroomsMappingResult,
  EleveMappingResult,
  MappingAction,
} from '@teetsh/app/src/containers/Eleves/Import/File/Finalize/ClassroomMapping/types/ClassroomActions';
import { Eleve } from '@teetsh/app/src/types/eleves';
import { Classroom } from '@teetsh/app/src/types/classroom';
import { ClassroomMatchInfo } from '@teetsh/app/src/containers/Eleves/Import/File/Finalize/matchClassrooms';
import { Mapping } from '@teetsh/app/src/containers/Eleves/Import/File/csv';
import { getElevesMapping } from '@teetsh/app/src/containers/Eleves/Import/File/Finalize/ClassroomMapping/getElevesMapping';
import { getClassroomsMapping } from '@teetsh/app/src/containers/Eleves/Import/File/Finalize/getClassroomsMapping';
import useClassrooms from '@teetsh/app/src/containers/App/context/ClassroomsContext/useClassrooms';
import { getGenderFromString } from '@teetsh/app/src/containers/Eleves/Import/File/Finalize/getElevesFromCsv';
import { DEFAULT_CYCLE, ScholarSystems } from 'containers/scholarSystem';

interface UpdateElevePayload {
  classroomId: string;
  eleve: Partial<Omit<Eleve, 'customFields>'>> & {
    customFields?: { [key: string]: string };
  };
}

function rowsToEleves(
  rows: object[],
  mappings: Mapping[] | null,
  eleves: Eleve[],
): EleveMappingResult[] {
  const elves = getElevesMapping(rows || [], mappings, eleves);
  return elves.map(ev => {
    const gender = getGenderFromString(ev.gender);
    return {
      id: ev.existingEleve ? ev.existingEleve.id : uuid(),
      existingEleve: ev.existingEleve,
      gender,
      action: ev.existingEleve ? ev.existingEleve.id : 'create',
      ine: ev.ine,
      birthday: ev.birthday,
      familyName: ev.familyName,
      givenName: ev.givenName,
      customFields: ev.customFields,
    };
  });
}

function classroomMatchInfoToClassroomsResult(
  classrooms: ClassroomMatchInfo[],
  mappings: Mapping[] | null,
  eleves: Eleve[],
): ClassroomsMappingResult {
  return {
    classrooms: classrooms.map(cl => {
      const newId = cl.match?.id || uuid();
      return {
        id: newId,
        idBe: cl.idBe || cl.match?.idBe || '',
        name: cl.name || '',
        existingClassroom: cl.match,
        action: cl.match ? cl.match.id : 'create',
        cycle:
          (cl.match?.cycle || []).length > 0
            ? [cl?.match?.cycle[0] as string]
            : [DEFAULT_CYCLE],
        degree: cl.degree || ScholarSystems.FR.defaultNiveau,
        eleves: rowsToEleves(cl?.rows || [], mappings, eleves),
      };
    }),
  };
}

type Action =
  | { type: 'update-eleve'; payload: UpdateElevePayload }
  | { type: 'reset'; payload: ClassroomsMappingResult }
  | {
      type: 'update-classroom';
      payload: {
        classroom: Pick<Classroom, 'id' | 'idBe'> & { action: MappingAction };
        eleves: Eleve[];
        mappings: Mapping[] | null;
      };
    };

function reducer(
  state: ClassroomsMappingResult,
  action: Action,
): ClassroomsMappingResult {
  switch (action.type) {
    case 'update-eleve':
      return {
        ...state,
        classrooms: state.classrooms.map(cl => {
          if (cl.id === action.payload.classroomId) {
            return {
              ...cl,
              eleves: cl.eleves.map(ev => {
                if (ev.id === action.payload.eleve.id) {
                  return {
                    ...ev,
                    ...action.payload.eleve,
                    customFields: action?.payload?.eleve?.customFields,
                  };
                }
                return ev;
              }),
            };
          }
          return cl;
        }),
      };
    case 'update-classroom':
      return {
        ...state,
        classrooms: state.classrooms.map(cl => {
          if (cl.id === action.payload.classroom.id) {
            // Only generate new UUID if action is changing TO 'create' (not already 'create')
            const needsNewId =
              action.payload.classroom.action === 'create' &&
              cl.action !== 'create';
            return {
              ...cl,
              ...action.payload.classroom,
              id: needsNewId ? uuid() : cl.id,
            };
          }
          return cl;
        }),
      };
    case 'reset': {
      return { classrooms: action.payload.classrooms };
    }
    default:
      throw new Error();
  }
}

export function useClassroomsMapping(
  rows: object[],
  mappings: Mapping[] | null,
  eleves: Eleve[],
) {
  const classrooms = useClassrooms();
  const classroomsMapping = getClassroomsMapping(rows, mappings, classrooms);
  const initialState = classroomMatchInfoToClassroomsResult(
    classroomsMapping,
    mappings,
    eleves,
  );
  const [state, dispatch] = useReducer(reducer, initialState);
  useEffect(() => {
    dispatch({
      type: 'reset',
      payload: classroomMatchInfoToClassroomsResult(
        classroomsMapping,
        mappings,
        eleves,
      ),
    });
  }, [(mappings || []).map(m => m.header).join(''), eleves]);
  const onUpdateEleve = useCallback(
    ({
      classroomId,
      eleve,
    }: {
      classroomId: string;
      eleve: Partial<Omit<Eleve, 'customFields>'>> & {
        action: MappingAction;
        customFields?: { [key: string]: string };
      };
    }) =>
      dispatch({
        type: 'update-eleve',
        payload: {
          classroomId,
          eleve,
        },
      }),
    [],
  );
  const onUpdateClassroom = useCallback(
    (classroom: Pick<Classroom, 'id' | 'idBe'> & { action: MappingAction }) =>
      dispatch({
        type: 'update-classroom',
        payload: { classroom, eleves, mappings },
      }),
    [],
  );
  return {
    state,
    reset: (st: ClassroomsMappingResult) =>
      dispatch({ type: 'reset', payload: st }),
    onUpdateClassroom,
    onUpdateEleve,
  };
}
